import React, { useEffect, useRef } from 'react';
import Player from '@vimeo/player';

export type VimeoEventData = {
  seconds: number;
  duration: number;
  percent: number;
};

interface VimeoPlayerProps {
  videoUrl: string;
  start?: number;
  speed?: boolean;
  onReady?: (data: { player: Player }) => void;
  onPlay?: (data: VimeoEventData) => void;
  onTimeUpdate?: (data: VimeoEventData) => void;
  onPause?: (data: VimeoEventData) => void;
  onProgress?: (data: VimeoEventData) => void;
  onSeeked?: (data: VimeoEventData) => void;
  onEnd?: (data: VimeoEventData) => void;
  onPlaybackRateChange?: (data: { playbackRate: number }) => void;
  onError?: (data: any) => void;
}

const VimeoPlayer: React.FC<VimeoPlayerProps> = ({
  videoUrl,
  start = 0,
  speed = false,
  onReady,
  onPlay,
  onTimeUpdate,
  onPause,
  onProgress,
  onSeeked,
  onEnd,
  onPlaybackRateChange,
  onError,
}) => {

  const containerRef = useRef<HTMLDivElement>(null);
  const playerRef = useRef<Player | null>(null);

  useEffect(() => {

    if (!containerRef.current) return () => {};

    let isMounted = true;

    const options: any = {
      url: videoUrl,
      speed,
    };

    const player = new Player(containerRef.current, options);
    playerRef.current = player;

    player.ready().then(() => {

      if (!isMounted) return;

      if (start > 0) {

        player.setCurrentTime(start).catch(() => {});

      }

      if (onReady) onReady({ player });

    })
      .catch((err) => {

        onError?.(err);

      });

    const eventMap: Record<string, ((data: any) => void) | undefined> = {
      play:               onPlay,
      pause:              onPause,
      ended:              onEnd,
      timeupdate:         onTimeUpdate,
      progress:           onProgress,
      seeked:             onSeeked,
      playbackratechange: onPlaybackRateChange,
      error:              (data) => {

        console.error('Vimeo Player Error:', data);
        onError?.(data);

      },
    };

    Object.entries(eventMap).forEach(([eventName, callback]) => {

      if (callback) {

        player.on(eventName, (data) => {

          if (isMounted) callback(data);

        });

      }

    });

    return () => {

      isMounted = false;
      if (playerRef.current) {

        Object.keys(eventMap).forEach((eventName) => playerRef.current?.off(eventName));

        playerRef.current.destroy().catch(() => {});
        playerRef.current = null;

      }

    };

  }, [videoUrl, speed]);

  return (
    <div
      ref={containerRef}
      style={{
        width:           '100%',
        aspectRatio:     '16/9',
        backgroundColor: '#000',
      }}
    />
  );

};

export default VimeoPlayer;
